import { useEffect, useState } from 'react';
import { Button, Upload, message, Form, Col, Row, Divider, Space, Typography, Drawer, Popconfirm } from 'antd';
import { CheckCircleOutlined, ContainerOutlined, UploadOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { ALLOWED_EXTENSIONS, ALLOWED_TYPES, applicationCode, drawerWidth, MAX_FILE_SIZE_MB, menuLinks } from '../../../utils/constants';
import { useUserAuth } from '../../../context/UserContext';
import { changeApplication, errorAlertDisplay } from '../../../utils/functions';
import CreditNotesServices from '../../../services/creditNotesServices';
const { Text, Title } = Typography;

const UploadCreditNoteModal = ({ visibility, onClose }: { visibility: boolean, onClose: () => void }) => {

    const [form] = Form.useForm();
    const { t } = useTranslation();
    const { user, setActiveApp, applications } = useUserAuth();
    const [uploadSuccess, setUploadSuccess] = useState(false);
    const [isFormValid, setIsFormValid] = useState(false);


    useEffect(() => {
        if (!visibility) {
            resetForm();
        } else {
            // Validate form when drawer opens
            handleFormChange();
        }
    }, [visibility]);

   
    const resetForm = () => {
        setUploadSuccess(false);
        setIsFormValid(false);
        form.resetFields();
    }

    const handleFormChange = () => {
        const allValues = form.getFieldsValue();

        // Check if any field has validation errors
        const errorList = form.getFieldsError();
        const hasErrors = errorList.some(({ errors }) => errors.length > 0);

            // 3. file field is an array, check if it has at least one item
        const hasFile = Array.isArray(allValues.file) && allValues.file.length > 0;

        // Form is valid if no errors and all required fields are filled
        setIsFormValid(!hasErrors && hasFile);
    };

    const normFile = (e: any) => {
        // Normalize file event to file list
        if (Array.isArray(e)) {
            return e;
        }
        return e?.fileList;
    };

    const beforeUpload = (file: any) => {
        const isExcelType = ALLOWED_TYPES.includes(file.type);
        const isExcelExt = ALLOWED_EXTENSIONS.some(ext =>
            file.name.toLowerCase().endsWith(ext)
        );

        if (!isExcelType && !isExcelExt) {
            message.error(t('messages.uploadTypeAllowed'));
            return Upload.LIST_IGNORE;
        }

        const isLt50MB = file.size / 1024 / 1024 <= MAX_FILE_SIZE_MB;
        if (!isLt50MB) {
            message.error(t('messages.uploadSizeLimit'));
            return Upload.LIST_IGNORE;
        }

        return true;
    };

    const onSubmitCreditNote = async () => {
        const values = form.getFieldsValue();

        const fileList = values.file;
        if (!fileList || fileList.length === 0) {
            message.error(t('messages.uploadFileEmpty'));
            return;
        }
        const file = fileList[0].originFileObj;
        const formData = new FormData();

        formData.append('File', file);
        formData.append('UserID', user.id);

        setUploadSuccess(false);
        try {
            const respData = await CreditNotesServices.uploadCreditNoteFile(formData);

            if (respData.success) {
                resetForm();
                setUploadSuccess(true);
            } else {
                // message.error(t('messages.errorAccured'));
                errorAlertDisplay(respData);
            }
        } catch (error) {
            message.error(t('messages.errorAccured'));
        }
    };

    const goToUploadLogs = async () => {
        let comApp = applications.filter((item: any) => item.code === applicationCode.bulkImport)[0];
        let payload = {
            applicationID: comApp.value,
            userID: user.id
        }
        try {
            localStorage.setItem("activeApp", comApp.value);
            setActiveApp(comApp.value);
            await changeApplication(payload);
            window.location.replace(menuLinks.importLogs);
            // navigate(menuLinks.importLogs);
        } catch (error) {
            message.error(t('messages.errorAccured'));
        }
    }

    return (
        <Drawer
            title={t('general.selectUpload')}
            width={drawerWidth}
            onClose={onClose}
            destroyOnClose={true}
            open={visibility}
        >

            {!uploadSuccess && (
                <Form
                    form={form}
                    layout='vertical'
                    name="uploadCreditNote"
                    autoComplete="off"
                    onFieldsChange={handleFormChange}
                >
                    <Row gutter={[16, 8]} className='mb10'>                     
                   
                        <Col xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 24 }} lg={{ span: 24 }}>
                            <Form.Item
                                label={t('general.file')}
                                name="file"
                                valuePropName="fileList"
                                getValueFromEvent={normFile}
                                rules={[{ required: true, message: t('messages.requiredField') }]}
                            >
                                <Upload
                                    accept=".xls,.xlsx,.csv"
                                    maxCount={1}
                                    beforeUpload={beforeUpload}
                                    showUploadList={true}
                                    customRequest={({ file, onSuccess }) => {
                                        setTimeout(() => {
                                            onSuccess?.("ok");
                                        }, 500);
                                    }}
                                >
                                    <Button icon={<UploadOutlined />}>{t('order.selectFile')}</Button>
                                </Upload>
                            </Form.Item>
                        </Col>
                    </Row>
                    <Divider />
                    <Form.Item>
                        <Popconfirm
                            title={t('order.confirmUpload')}
                            onConfirm={() => onSubmitCreditNote()}
                            okText={t('general.yes')}
                            cancelText={t('general.no')}
                        >
                            <Button disabled={!isFormValid} htmlType='submit' block icon={<UploadOutlined />}>{t('order.startUpload')}</Button>
                        </Popconfirm>
                    </Form.Item>
                </Form>
            )}

            {uploadSuccess && (
                <Row gutter={[16, 8]} className='mb10'>
                    <Col xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 24 }} lg={{ span: 24 }} style={{ display: 'flex', justifyContent: 'center' }}>
                        <Space direction='vertical' align='center'>
                            <CheckCircleOutlined style={{ color: "#09b944", fontSize: 35 }} />
                            <Title level={5}>
                                {t('order.fileSubmitedCheck')}:
                                <Button onClick={() => goToUploadLogs()} style={{ marginLeft: 10 }} type="dashed" size='small' icon={<ContainerOutlined style={{ fontSize: 15 }} />}>
                                    {t('order.viewLogs')}
                                </Button>
                            </Title>
                        </Space>
                    </Col>
                </Row>
            )}
        </Drawer>
    );
}
export default UploadCreditNoteModal;